import React, { useMemo, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  moduleBySlug,
  dashboardBySlugs,
  neighborDashboards,
  buildSisenseUrl,
} from "../config/modules";

export default function ModuleDashboard() {
  const { moduleSlug, dashboardSlug } = useParams();
  const navigate = useNavigate();

  // Look up module and dashboard (always call hooks before any return)
  const mod = useMemo(() => moduleBySlug(moduleSlug), [moduleSlug]);
  const info = useMemo(
    () => (mod ? dashboardBySlugs(moduleSlug, dashboardSlug) : null),
    [mod, moduleSlug, dashboardSlug]
  );

  // Compute neighbors
  const { prev, next } = useMemo(() => {
    if (!mod || !info) return { prev: null, next: null };
    return neighborDashboards(moduleSlug, dashboardSlug);
  }, [mod, info, moduleSlug, dashboardSlug]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft" && prev) navigate(`/m/${moduleSlug}/${prev.slug}`);
      if (e.key === "ArrowRight" && next) navigate(`/m/${moduleSlug}/${next.slug}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, moduleSlug, navigate]);

  // ---- Render ----
  if (!mod) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center text-white bg-black">
        <div>
          <p className="text-xl mb-4">Módulo no encontrado.</p>
          <Link to="/" className="underline">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center text-white bg-black">
        <div>
          <p className="text-xl mb-4">Dashboard no encontrado.</p>
          <Link to={`/m/${moduleSlug}`} className="underline">Volver al módulo</Link>
        </div>
      </div>
    );
  }

  const { dashboard, index, total } = info;
  const src = buildSisenseUrl({
    host: mod.host,
    dashboardId: dashboard.sisenseDashboardId,
    query: dashboard.query,
  });

  return (
    <div className="fixed inset-0 bg-black">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 h-12 px-3 sm:px-4 flex items-center justify-between gap-2 bg-black/70 backdrop-blur z-10">
        {/* Left: Back + module */}
        <div className="flex items-center gap-3">
          <Link to="/" className="text-white/90 hover:text-white underline">← Volver</Link>
          <span className="hidden sm:inline text-white/70">/ {mod.title}</span>
        </div>

        {/* Center: Dashboard buttons */}
        <div className="flex-1 flex items-center justify-center">
          <nav
            aria-label="Dashboards"
            className="flex items-center gap-3 overflow-x-auto px-2"
          >
            {mod.dashboards.map((d) => {
              const active = d.slug === dashboardSlug;
              return (
                <button
                  key={d.slug}
                  onClick={() => navigate(`/m/${moduleSlug}/${d.slug}`)}
                  aria-current={active ? "page" : undefined}
                  className={[
                    "whitespace-nowrap rounded-full px-4 py-1.5 text-sm border transition",
                    "focus:outline-none focus:ring-2 focus:ring-white/40",
                    active
                      ? "bg-blue-600 border-blue-500 text-white shadow"
                      : "bg-transparent border-white/25 text-white/80 hover:bg-white/10"
                  ].join(" ")}
                  title={d.title}
                >
                  {d.title}
                </button>
              );
            })}
          </nav>
          <span className="ml-3 text-white/60 text-xs hidden sm:inline">
            {index + 1} / {total}
          </span>
        </div>

        {/* Right: Prev / Next */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => prev && navigate(`/m/${moduleSlug}/${prev.slug}`)}
            disabled={!prev}
            className={`px-3 py-1 rounded border ${prev ? "border-white/30 text-white/90 hover:bg-white/10" : "border-white/10 text-white/40 cursor-not-allowed"}`}
            title="Anterior (←)"
          >
            Anterior
          </button>
          <button
            onClick={() => next && navigate(`/m/${moduleSlug}/${next.slug}`)}
            disabled={!next}
            className={`px-3 py-1 rounded border ${next ? "border-white/30 text-white/90 hover:bg-white/10" : "border-white/10 text-white/40 cursor-not-allowed"}`}
            title="Siguiente (→)"
          >
            Siguiente
          </button>
        </div>
      </div>

      {/* Full-screen iframe */}
      <iframe
        title={`${mod.title} - ${dashboard.title}`}
        src={src}
        className="absolute top-12 left-0 w-screen h-[calc(100vh-3rem)]"
        frameBorder="0"
        scrolling="auto"
        allow="clipboard-write *"
        allowFullScreen
      />
    </div>
  );
}
